import compareDesc from "date-fns/compareDesc";
import { useQuery } from "@apollo/client";
import { useContext } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { useIntl } from "react-intl";

import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import LocaleContext from "components/i18n/LocaleContext";
import CorporateActionsContent from "components/company/sections/content/CorporateActionsContent";

import { palette } from "lib/miolo";
import GlobalCorporateActionsQuery from "lib/queries/GlobalCorporateActionsQuery";
import type {
  IGlobalCorporateActionsQuery,
  IGlobalCorporateActionsQueryVariables,
} from "lib/queries/__generated__/GlobalCorporateActionsQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import parseDateYmd from "lib/utils/parseDateYmd";
import type {
  IRightsIssuesCorporateAction,
  ISpinOffCorporateAction,
  IStockSplitCorporateAction,
} from "types/graphqlTypes";
import { ISecurityCountry } from "types/graphqlTypes";
import ContentSectionBreadcrumbs from "../ContentSectionBreadcrumbs";
import CorporateActionsSummary from "../summary/CorporateActionsSummary";

interface IAmericanCompanySectionsProps {
  tickerSymbols: string[];
  isExpanded: boolean;
  onToggleCollapsed: () => void;
}

export default function CorporateActionsCollapsible({
  tickerSymbols,
  isExpanded,
  onToggleCollapsed,
}: IAmericanCompanySectionsProps) {
  const intl = useIntl();
  const locale = useContext(LocaleContext);
  const isUSALocale = locale === "USA";

  const { data, loading } = useQuery<IGlobalCorporateActionsQuery, IGlobalCorporateActionsQueryVariables>(
    GlobalCorporateActionsQuery,
    {
      variables: {
        tickerSymbols,
        securityCountry: isUSALocale ? ISecurityCountry.Us : ISecurityCountry.Br,
      },
    },
  );

  const segments = [
    intl.formatMessage({
      defaultMessage: "Governança",
      id: "/nZpWX",
      description: "Eventos Corporativos",
    }),
    intl.formatMessage({ defaultMessage: "Eventos Corporativos", id: "/fMk6a", description: "Eventos Corporativos" }),
  ];

  const hasStockSplit = data && data.stockSplit.length > 0;
  const hasRightsIssues = data && data.rightsIssues.length > 0;
  const hasSpinOff = data && data.spinOff.length > 0;

  if (loading) {
    return <ClipLoader size={28} color={palette.royalBlue} loading={true} />;
  }

  if (hasStockSplit || hasRightsIssues || hasSpinOff) {
    return (
      <CollapsibleContentSection
        title={intl.formatMessage({
          defaultMessage: "Eventos Corporativos",
          id: "/fMk6a",
          description: "Eventos Corporativos",
        })}
        isExpanded={isExpanded}
        isPremiumSection={true}
        onToggleCollapsed={onToggleCollapsed}
        primaryColor={palette.mariner}
        renderSubtitle={() => <ContentSectionBreadcrumbs segments={segments} />}
        renderContentSummary={() => {
          const [event] = getLastEventOfCorporateActions(data);
          const name = getContentSummaryDisplayName(event, isUSALocale);

          return <CorporateActionsSummary date={event.excludingDate} name={name} />;
        }}
        renderContent={() => (
          <CorporateActionsContent
            tickerSymbols={tickerSymbols}
            data={data}
            isUSALocale={isUSALocale}
            isLoading={loading}
          />
        )}
      />
    );
  }

  return null;
}

function getContentSummaryDisplayName(
  event: IStockSplitCorporateAction | IRightsIssuesCorporateAction | ISpinOffCorporateAction,
  isUSALocale: boolean,
) {
  if (event.__typename === "RightsIssuesCorporateAction") {
    return isUSALocale ? "Rights Issues" : "Subscrição";
  }

  if (event.__typename === "SpinOffCorporateAction") {
    return isUSALocale ? "Spin Off" : "Cisão";
  }

  return isUSALocale ? "Stock Split" : "Desdobramento";
}

function getLastEventOfCorporateActions(data: IGlobalCorporateActionsQuery | undefined) {
  const [lastStockSplit] = data?.stockSplit ?? [];
  const [lastRightsIssues] = data?.rightsIssues ?? [];
  const [lastSpinOff] = data?.spinOff ?? [];

  const arrayOfLastEvents = [lastStockSplit, lastRightsIssues, lastSpinOff]
    .filter(notUndefinedOrNull)
    .sort((a, b) => compareDesc(parseDateYmd(a.excludingDate), parseDateYmd(b.excludingDate)));

  return arrayOfLastEvents;
}
